import React from 'react';
import PropTypes from 'prop-types';
import {
    WeezHeader,
    Button,
    Modal,
    AnchorNav,
    Form,
    Icon,
    Card,
    ContentCard,
    FrameCard,
    SplitContainer,
    Table,
    Toggle,
    SelectButton,
    Tooltip,
    Dropdown,
    TooltipHelp,
    PreviewCard,
    ToastContext
} from '@weezevent/nacre';

import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useForm, requiredField } from '../../utils/validators';
import { capitalizeFirstLetter } from '../attendee/detail/components/utils';
import FormModel from '../../models/form';
import FieldModel from '../../models/form_field';
import Loading from '../../components/loading';
import { fetcher } from '@weezevent/weezjs-core';
import ModalFields from './components/ModalFields/modalFields';
import ModalCustomField from './components/ModalFields/sub_components/modalCustomField';
import Style from './style/details.module.css';
import StyleRM from '../rates/components/rateManager.module.css';
import { InheritedProperty } from './../rates/ratesList';
import RatesManager, { generateClassNameRateName } from '../rates/components/rateManager';
import { RateCategorySeat } from '../rates/components/rateCategorySeat';
import { FormPreview } from '../forms/components/formPreview';
import useAppContext from '../../context/useAppContext';
import FormEntry from '../../models/form_entry';
import ResponsiveContainer from './components/responsiveContainer';
import { usePermissions } from '../../user/permissions';
import { useSegment, useTrackingPage } from '../../components/tracking/trackingProvider';
import { LANGUAGES } from '../../components/translations/translationModal';

const displayHiddenColumn = organization => {
    /**
     * This function is used to know if organizer has the feature flag which allow him to
     * see the "hide" column and toggles in fields
     */
    return organization.feature_flags.includes('EO_HIQUE');
};

const displayModificationColumn = organization => {
    /**
     * This function is used to know if organizer has the feature flag which allow him to
     * see the "modification" column and toggles in fields
     */
    return organization.feature_flags.includes('EO_MAJLINK') || organization.feature_flags.includes('EO_PAYLINK');
};

export function useFetchFields(eventId, organizationId, callback = () => {}) {
    const [data, setData] = React.useState({});
    const [loading, setLoading] = React.useState(true);
    const {
        i18n: { language }
    } = useTranslation();
    React.useEffect(() => {
        fetcher
            .get(`/ticket/organizations/${organizationId}/events/${eventId}/fields`, {
                query: {
                    displayable: true
                }
            })
            .then(({ response: { custom_types, fields, default_form } }) => {
                fields = FieldModel.list(fields, language);
                default_form = FormModel.fromJSON(default_form, language);
                callback(default_form, fields);
                setData({ custom_types, fields, default_form });
            })
            .finally(() => {
                setLoading(false);
            });
    }, [eventId, organizationId]);

    return [data, loading];
}

function FormCardContentHeader({ selectLabel, selectTooltip, selectObjects, addLabel, onClick, children }) {
    return (
        <div className={Style['details__container-actions']}>
            <SelectButton title={selectLabel} tooltipTitle={selectTooltip} objects={selectObjects}>
                {children}
            </SelectButton>
            <Button inverted primary className={Style['details__add-button']} onClick={onClick}>
                <Icon name={'plus'} className={Style['plus']} />
                {addLabel}
            </Button>
        </div>
    );
}

function FieldsCartContentRowToggleCell({ onToggle, field, attribute, isDefault }) {
    const { t } = useTranslation();

    const tooltipTitle = React.useMemo(() => {
        if (field.form_field.field_type === 'divider') {
            if (attribute === 'required') {
                return t('ticket.form.details.common.toggle_required_divider');
            }
            if (attribute === 'allow_modification') {
                return t('ticket.form.details.common.toggle_allow_modification_divider');
            }
        }
        return isDefault ? t('ticket.form.details.common.toggle_mandatory') : t('ticket.form.details.common.toggle_hidden');
    }, [field, attribute, isDefault]);

    const disabled = React.useMemo(() => {
        if (attribute === 'required' && (field.form_field.weez_id === 'ws-50' || field.form_field.field_type === 'divider')) {
            // Opt_in field cannot be required.
            return true;
        }

        if (attribute === 'allow_modification') {
            return Boolean(field.form_field.field_type === 'divider');
        }
        if (isDefault) {
            return true;
        }
        if (attribute === 'show_only_bo') {
            return Boolean(field['required']);
        }
        return false;
    }, [field, attribute]);

    return (
        <Table.Td className={Style['table-td']}>
            <Tooltip triggerOnHover visible={disabled} title={tooltipTitle} className={`${Style['tooltip']} ${Style['toggle']}`}>
                <Toggle
                    className={`${Style[disabled ? 'disabled-toggle' : '']}`}
                    onChange={(_, { checked }) => onToggle(attribute, checked)}
                    checked={field[attribute] === true}
                    disabled={disabled}
                />
            </Tooltip>
        </Table.Td>
    );
}

function FieldsCardContentRowActionCell({ onDelete, onEdit, isDefault, field, isRequiredField = false }) {
    const { t } = useTranslation();
    const { track } = useSegment();
    const permissions = usePermissions();

    const disableTooltip = React.useMemo(() => {
        return !isDefault || isRequiredField;
    }, [isDefault, requiredField]);

    const toolTipContent = React.useMemo(() => {
        if (!isRequiredField || permissions.isAdmin()) {
            return t('ticket.common.action.delete');
        }
        return t('ticket.form.details.buyer.required');
    }, [isRequiredField, permissions]);

    return (
        <div className={Style['actions']}>
            <Tooltip
                triggerOnHover
                margin={false}
                title={t('ticket.common.action.edit')}
                className={`${Style['tooltip']} ${Style[field.form_field.is_standard ? 'standard' : 'custom']}`}
            >
                <Icon name="cog" onClick={onEdit} className={`${Style['cog']} ${field.form_field.is_standard ? Style['disabled'] : ''}`} />
            </Tooltip>
            <Tooltip margin={false} triggerOnHover={disableTooltip} title={toolTipContent}>
                <Icon
                    name="cross"
                    disabled={isDefault && !permissions.isAdmin()}
                    onClick={() => {
                        track('ACTION > DELETE FIELD', {
                            'Page Title': 'CREATE FORM',
                            'Target type': capitalizeFirstLetter(field.usage),
                            'Field name': field.form_field.label
                        });
                        onDelete();
                    }}
                    className={Style['cross']}
                />
            </Tooltip>
        </div>
    );
}

function FieldsCardContentRow({ field, checked, onCheck, onToggle, onDelete, onEdit, index, isDefault }) {
    const {
        i18n: { language }
    } = useTranslation();
    const [{ organization }] = useAppContext();

    const isRequiredField = React.useMemo(() => {
        const requiredFields = ['ws-2', 'ws-3', 'ws-5', 'ws-36'];
        return field.usage === 'buyer' && requiredFields.includes(field.form_field.weez_id);
    }, [field]);

    const toggles = React.useMemo(() => {
        let toggle = ['required'];
        if (displayHiddenColumn(organization)) {
            toggle.push('show_only_bo');
        }
        if (displayModificationColumn(organization)) {
            toggle.push('allow_modification');
        }
        return toggle;
    }, [organization, displayHiddenColumn, displayModificationColumn]);

    return (
        <Table.Tr index={index} active={checked} onSelected={onCheck} className={Style['row']}>
            <Table.Td className={Style['td-name']}>{field.form_field.get_translated_label(language.toLowerCase())}</Table.Td>
            {toggles.map(attribute => (
                <FieldsCartContentRowToggleCell key={`${attribute}_${field.id}`} onToggle={onToggle} field={field} attribute={attribute} isDefault={isDefault} />
            ))}
            <Table.Td>
                <FieldsCardContentRowActionCell onDelete={onDelete} onEdit={onEdit} isDefault={isDefault} field={field} isRequiredField={isRequiredField} />
            </Table.Td>
        </Table.Tr>
    );
}

function FieldsCardContent({ objects: form_entries, onToggle, onClick, onDelete, onEdit, onDragAndDrop, defaultFields }) {
    const { t } = useTranslation();
    const { track } = useSegment();
    const [selectedFields, setSelectedFields] = React.useState([]);
    const [{ organization }] = useAppContext();
    const permissions = usePermissions();

    const displayHiddenColumns = displayHiddenColumn(organization);
    const displayModificationColumns = displayModificationColumn(organization);

    const handleSelectedFields = React.useCallback(
        (checked, field, allSelected = false) => {
            if (allSelected) {
                return setSelectedFields(checked ? [...form_entries.map(field => field.order)] : []);
            }
            const newSelectedFields = [...selectedFields];
            if (!checked) {
                const fieldIndex = selectedFields.findIndex(f => f === field.order);
                newSelectedFields.splice(fieldIndex, 1);
                return setSelectedFields(newSelectedFields);
            }
            newSelectedFields.push(field.order);
            return setSelectedFields(newSelectedFields);
        },
        [selectedFields, form_entries]
    );

    const handleDragAndDrop = React.useCallback(
        ({ newIndex, oldIndex }) => {
            const tmp_entries = [...form_entries];
            const entryToMove = tmp_entries[oldIndex];
            tmp_entries.splice(oldIndex, 1);
            tmp_entries.splice(newIndex, 0, entryToMove);
            // Put some good orders every where cause we never know what is happening in weezevent
            tmp_entries.forEach((entry, i) => {
                entry.order = i;
            });
            onDragAndDrop(tmp_entries);
            const field = tmp_entries.find(f => f.order === newIndex);
            track('ACTION > DRAG & DROP', {
                'Page Title': 'CREATE FORM',
                'Target type': capitalizeFirstLetter(field.usage),
                'Field name': field.form_field.label,
                'Field position': newIndex
            });
        },
        [form_entries, onDragAndDrop]
    );

    const handleDeleteFields = React.useCallback(
        selectedFields => {
            onDelete([...form_entries.filter(f => selectedFields.includes(f.order))]);
            setSelectedFields([]);
        },
        [onDelete]
    );

    const disableActions = React.useMemo(() => {
        if (permissions.isAdmin()) {
            // Allow administrators to perform any actions on form fields
            return false;
        }
        const requiredFields = ['ws-2', 'ws-3', 'ws-5', 'ws-36'];
        return Boolean([...form_entries.filter(f => f.usage === 'buyer' && selectedFields.includes(f.order) && requiredFields.includes(f.form_field.weez_id))].length);
    }, [selectedFields, form_entries, permissions]);

    return (
        <>
            <ContentCard>
                <FormCardContentHeader
                    selectObjects={selectedFields}
                    selectLabel={t('ticket.form.details.select_interval', { postProcess: 'interval', count: selectedFields.length })}
                    selectTooltip={t('ticket.form.details.select_tooltip')}
                    addLabel={t('ticket.form.details.attendee.label')}
                    onClick={onClick}
                >
                    <Dropdown.Item deleteItem onClick={() => handleDeleteFields(selectedFields)} disabled={disableActions}>
                        {t('ticket.common.action.delete')}
                    </Dropdown.Item>
                </FormCardContentHeader>
            </ContentCard>
            <ResponsiveContainer>
                <div className={Style['trigger_scroll_table']}>
                    <Table
                        responsive={false}
                        useNew
                        draggable
                        onSortEnd={handleDragAndDrop}
                        onSelectAll={(event, { checked }) => handleSelectedFields(checked, null, true)}
                        selectable
                        className={Style['table']}
                    >
                        <Table.THead className={Style['header']}>
                            <Table.Tr className={Style['table-row']} active={selectedFields.length === form_entries.length}>
                                {/* headers will be dynamically rendered once we handle premium options */}
                                <th>{t('ticket.common.fieldName')}</th>
                                <th>
                                    {t('ticket.common.mandatory')}
                                    <TooltipHelp
                                        // For now, because we can't handle dropdown/tooltip in a responsive array, remove position when we will
                                        position="bottom"
                                        triggerOnHover
                                        title={t('ticket.form.details.tooltip.mandatory')}
                                        className={Style['tooltip']}
                                    />
                                </th>
                                {displayHiddenColumns && (
                                    <th>
                                        {t('ticket.common.hidden')}
                                        <TooltipHelp
                                            // For now, because we can't handle dropdown/tooltip in a responsive array, remove position when we will
                                            position="bottom"
                                            triggerOnHover
                                            title={t('ticket.form.details.tooltip.hidden')}
                                            className={Style['tooltip']}
                                        />
                                    </th>
                                )}
                                {displayModificationColumns && (
                                    <th>
                                        {t('ticket.common.modifiable')}
                                        <TooltipHelp
                                            // For now, because we can't handle dropdown/tooltip in a responsive array, remove position when we will
                                            position="bottom"
                                            triggerOnHover
                                            title={t('ticket.form.details.tooltip.modifiable')}
                                            className={Style['tooltip']}
                                        />
                                    </th>
                                )}
                                <th />
                            </Table.Tr>
                        </Table.THead>
                        <Table.TBody className={Style['body']}>
                            {form_entries
                                .sort((field_1, field_2) => field_1.order - field_2.order)
                                .map((field, i) => (
                                    <FieldsCardContentRow
                                        index={i}
                                        key={`${field.usage}-${i}`}
                                        field={field}
                                        onCheck={(event, { checked }) => handleSelectedFields(checked, field)}
                                        onToggle={(attribute, value) => {
                                            onToggle(field.order, attribute, value);
                                            track('ACTION > MANDATORY TOGGLE', {
                                                'Page Title': 'CREATE FORM',
                                                'Target type': capitalizeFirstLetter(field.usage),
                                                'Field name': field.form_field.label,
                                                Activated: field.required
                                            });
                                        }}
                                        checked={selectedFields.some(f_order => f_order === field.order)}
                                        onDelete={() => handleDeleteFields([field.order])}
                                        onEdit={() => onEdit(field)}
                                        isDefault={defaultFields.includes(field.form_field.weez_id)}
                                    />
                                ))}
                        </Table.TBody>
                    </Table>
                </div>
            </ResponsiveContainer>
        </>
    );
}

function FormCard({ id, title, subtitle, emptyTitle, emptySubtitle, labelButton, onClick, objects, children, ...rest }) {
    const newChildren = React.Children.map(children, child => {
        if (child === null) {
            return null;
        }
        return React.cloneElement(child, { ...child?.props, objects, onClick, ...rest });
    });

    return (
        <Card id={id}>
            <Card.Header>
                <Card.Title title={title} />
                <Card.SubTitle title={subtitle} />
            </Card.Header>
            {objects.length ? newChildren : <Card.EmptyState title={emptyTitle} subtitle={emptySubtitle} icon={<Icon name="list" />} labelButton={labelButton} onClick={onClick} />}
        </Card>
    );
}
FormCard.propTypes = {
    id: PropTypes.string,
    title: PropTypes.string,
    subtitle: PropTypes.string,
    emptyTitle: PropTypes.string,
    onClick: PropTypes.func,
    objects: PropTypes.array,
    children: PropTypes.node,
    emptySubtitle: PropTypes.string,
    labelButton: PropTypes.string
};

export default function Details({ event, organization, formId = null }) {
    const { t } = useTranslation();
    const { track } = useSegment();
    const history = useHistory();
    const [usage, setDisplayFieldsModal] = React.useState(null);
    const [customFieldToEdit, setCustomFieldToEdit] = React.useState(null);
    const toast = React.useContext(ToastContext);

    //states rates section
    const [showModalRates, setShowModalRates] = React.useState(false);
    const [ratesToUnlink, setRatesToUnlink] = React.useState([]);
    const [ratesToLink, setRatesToLink] = React.useState([]);
    const [currentSelectedToLink, setCurrentSelectedToLink] = React.useState([]);
    const [currentSelectedToUnlink, setCurrentSelectedToUnlink] = React.useState([]);
    const [isSubmitting, setIsSubmitting] = React.useState(false);

    const [formState, onChangeForm, onChangeFormObject, formErrors, formValidate] = useForm(
        {
            name: '',
            form_entries: [],
            rates: []
        },
        {
            name: requiredField
        }
    );

    const [, loading] = FormModel.useApiModel({
        launch: Boolean(formId),
        defaultLoading: Boolean(formId),
        eventId: event.pk,
        organizationId: organization.pk,
        id: formId,
        onSuccess: response => {
            const form = response.toAPI();
            const form_entries = form.form_entries.map(entry => {
                if (entry.form_field.field_type === 'divider') {
                    entry.required = false;
                    entry.allow_modification = false;
                }
                return entry;
            });
            onChangeFormObject({ ...form, form_entries });
        }
    });

    useTrackingPage('VIEW - CREATE FORM', { 'Page Title': 'CREATE FORM' });

    // Allow to set default fields in form when we create a new form
    const handleDefaultForm = React.useCallback(
        (defaultForm, fields) => {
            if (formId) {
                return;
            }
            const tmpEntries = defaultForm.form_entries.map(entry => {
                delete entry.id;
                return entry;
            });

            if (!tmpEntries.find(entry => entry.usage === 'attendee')) {
                // No form attendee, we have to add the first name and the last name, according TIK-5455.
                const firstName = fields.find(field => field.weez_id === 'ws-3');
                const attendeeFirstName = new FormEntry({
                    form_field: firstName,
                    usage: 'attendee',
                    order: 1,
                    required: true,
                    show_only_bo: false,
                    deletable: true,
                    allow_modification: true
                });
                const lastName = fields.find(field => field.weez_id === 'ws-2');
                const attendeeLastName = new FormEntry({
                    form_field: lastName,
                    usage: 'attendee',
                    order: 2,
                    required: true,
                    show_only_bo: false,
                    deletable: true,
                    allow_modification: true
                });
                tmpEntries.push(attendeeFirstName, attendeeLastName);
            }

            onChangeForm('form_entries', tmpEntries);
        },
        [formId, onChangeForm]
    );

    const [{ custom_types, fields, default_form }, loadingFields] = useFetchFields(event.pk, organization.pk, handleDefaultForm);

    const tableCells = [
        {
            header: t('ticket.common.action.select-all'),
            renderRate: (rate, { groups, categories }) => {
                return (
                    <InheritedProperty
                        property="name"
                        object={rate}
                        handleProperty={name => (
                            <div className={StyleRM[generateClassNameRateName(rate, groups)]} style={{ display: 'flex', alignItems: 'center' }}>
                                <RateCategorySeat event={event} rate={rate} categories={categories} />
                                {name}
                                {rate.form && formId && String(rate.form) !== String(formId) && (
                                    <Tooltip visible={true} triggerOnHover={true} margin={false} title={t('ticket.form.details.rates.modal.iconTooltipLink')}>
                                        <Icon className={StyleRM['rates-table__cell-group-icon-link']} name="link" />
                                    </Tooltip>
                                )}
                            </div>
                        )}
                        template="rate_template"
                    />
                );
            },
            renderGroup: (group, onToggle) => {
                return (
                    <div className={StyleRM['rates-table__cell-group']} onClick={() => onToggle(group)}>
                        {group.parent && <Icon className={StyleRM['rates-table__cell-group-icon-subgroup']} name="link-group" />}
                        <Icon name={group.showRates ? 'folder-open' : 'folder-closed'} style={{ marginRight: '5px' }} />
                        <InheritedProperty displayIcon={false} property="name" object={group} handleProperty={name => <b>{name}</b>} template="group_template" />
                    </div>
                );
            }
        },
        {
            className: Style['rates-table__cell-rate-delete'],
            renderRate: rate => {
                return (
                    <Tooltip visible={true} triggerOnHover={true} margin={false} title={t('ticket.common.action.unlink')}>
                        <Icon
                            className={Style['cross']}
                            name={'cross'}
                            onClick={() => {
                                unlink([rate]);
                                track('ACTION > UNBIND RATE', { 'Page Title': 'CREATE FORM' });
                            }}
                        />
                    </Tooltip>
                );
            },
            renderGroup: () => {
                return null;
            }
        }
    ];

    const tableCellsModal = [
        {
            header: t('ticket.common.action.select-all'),
            renderRate: (rate, { groups, categories }) => {
                return (
                    <InheritedProperty
                        property="name"
                        object={rate}
                        handleProperty={name => (
                            <div className={StyleRM[generateClassNameRateName(rate, groups)]} style={{ display: 'flex', alignItems: 'center', flexGrow: '1' }}>
                                <RateCategorySeat event={event} rate={rate} categories={categories} />
                                {name}
                                {rate.form && String(rate.form) !== String(formId) && (
                                    <Tooltip visible={true} triggerOnHover={true} margin={false} title={t('ticket.form.details.rates.modal.iconTooltipLink')}>
                                        <Icon className={StyleRM['rates-table__cell-group-icon-link']} name="link" onClick={() => {}} />
                                    </Tooltip>
                                )}
                            </div>
                        )}
                        template="rate_template"
                    />
                );
            },
            renderGroup: (group, onToggle) => {
                return (
                    <div className={StyleRM['rates-table__cell-group']} onClick={() => onToggle(group)}>
                        {group.parent && <Icon className={StyleRM['rates-table__cell-group-icon-subgroup']} name="link-group" />}
                        <Icon name={group.showRates ? 'folder-open' : 'folder-closed'} style={{ marginRight: '5px' }} />
                        <InheritedProperty displayIcon={false} property="name" object={group} handleProperty={name => <b>{name}</b>} template="group_template" />
                    </div>
                );
            }
        }
    ];

    const goBack = React.useCallback(() => history.push(`/ticket/O${organization.id}/E${event.id}/settings/forms`), [organization, event]);

    const handleStateField = React.useCallback(
        (field_order, attribute, value, usage) => {
            const tmp_fields = [...formState.form_entries];
            const fieldIndex = tmp_fields.findIndex(f => f.order === field_order && f.usage === usage);
            tmp_fields[fieldIndex][attribute] = value;
            if (attribute === 'required' && value === true) {
                tmp_fields[fieldIndex]['show_only_bo'] = false;
            }
            return onChangeForm(
                'form_entries',
                tmp_fields.map(f => f.copy())
            );
        },
        [formState]
    );

    const handleDeleteField = React.useCallback(
        fieldsToDelete => {
            // Allow to get the usage of the fields we want to delete
            const usage = fieldsToDelete[0].usage;
            // Get order of fields to delete without ID (it means we delete them from form_entries array)
            const fieldsOrderToRemove = fieldsToDelete.filter(f => !f.id).map(f => f.order);
            // Get order of fields to delete with ID (it means we add them a deleted flag)
            const fieldsOrderToAddDeletedFlags = fieldsToDelete.filter(f => f.id).map(f => f.order);
            const tmp_fields = [...formState.form_entries].filter(field => {
                // Not the right usage so we keep the field
                if (field.usage !== usage) {
                    return true;
                }
                // Part of the fields without id, delete it from array
                if (fieldsOrderToRemove.includes(field.order)) {
                    return false;
                }
                // Part of the fields with id, so we add a deleted flag
                if (fieldsOrderToAddDeletedFlags.includes(field.order)) {
                    field.deleted = true;
                }
                return true;
            });
            return onChangeForm('form_entries', tmp_fields);
        },
        [formState, onChangeForm]
    );

    // Function that will handle fields adding in the add fiels modal for buyer and attendee
    const handleStandardFields = React.useCallback(
        fields => {
            //Every fields sent by the modale are gonna replace the existing fields
            // remove standard fields non deleted from form_entries with current usage before adding the new fields
            const tmpFields = formState.form_entries.filter(field => !(field.form_field.is_standard && field.usage === usage) || field.deleted);

            let maxOrder = formState.form_entries.filter(entry => entry.usage === usage).reduce((max, entry) => Math.max(max, entry.order), 0);

            //we need to get the list of fields that were in standard fields but not anymore but that exists in db so we have to add a deleted flag
            // so to make it easier we are gonna build an array of new fields id
            const newFieldsId = [];
            fields.forEach(field => {
                if (field.id) {
                    newFieldsId.push(field.id);
                }
                if (field.order) {
                    return;
                }
                field.order = ++maxOrder;
            });
            tmpFields.push(...fields);

            const previousFieldsToDelete = formState.form_entries.filter(field => {
                // field is not a standard field with the correct usage, or a field without id or it s deleted
                if (!field.form_field.is_standard || field.usage !== usage || !field.id || field.deleted) {
                    return false;
                }
                // check if the field id is part of the newfieldsId
                // if yes, we already got this field so we don't want it anymore
                if (newFieldsId.includes(field.id)) {
                    return false;
                }
                field.deleted = true;
                return true;
            });
            tmpFields.push(...previousFieldsToDelete);

            onChangeForm('form_entries', tmpFields);
            return setDisplayFieldsModal(null);
        },
        [formState, onChangeForm, usage]
    );

    // will handle display the custom field modal on its own ?
    const handleDisplayEditCustomFieldModal = React.useCallback((field, usage) => {
        setCustomFieldToEdit(field);
        setDisplayFieldsModal(usage);
    }, []);

    const handleCustomField = React.useCallback(
        custom_field => {
            const tmpFields = [...formState.form_entries];
            // check if the field got an order
            // if yes: it's an edition
            if (custom_field.order) {
                const fieldIndex = formState.form_entries.findIndex(entry => entry.order === custom_field.order && entry.usage === custom_field.usage);
                tmpFields[fieldIndex] = custom_field;
                return onChangeForm('form_entries', tmpFields);
            }
            //Otherwise we are adding a new field
            //Look for max order in the fields with right usage
            const newOrder = tmpFields.filter(entry => entry.usage === custom_field.usage).reduce((max, entry) => Math.max(max, entry.order), 0) + 1;
            custom_field.order = newOrder;
            tmpFields.push(custom_field);
            return onChangeForm('form_entries', tmpFields);
        },
        [onChangeForm, formState]
    );

    const handleDragAndDrop = React.useCallback(
        (fields, usage) => {
            return onChangeForm('form_entries', [...formState.form_entries.filter(f => f.usage !== usage), ...fields]);
        },
        [formState, onChangeForm]
    );

    const ratesToSubmit = React.useMemo(() => {
        const idsRatesToLink = ratesToLink.map(rate => rate.pk);
        const idsRatesToUnlink = ratesToUnlink.map(rate => rate.pk);
        return [...formState.rates, ...idsRatesToLink].filter(rate => !idsRatesToUnlink.includes(rate));
    }, [formState, ratesToLink, ratesToUnlink]);

    const handleSubmit = React.useCallback(() => {
        if (!formValidate()) {
            return toast.error(t('ticket.form.details.submit.validation_error'));
        }
        setIsSubmitting(true);
        const form = new FormModel({
            name: formState.name,
            form_entries: formState.form_entries.map(entry => entry.toAPI()),
            rates: ratesToSubmit,
            event: event.pk
        });
        if (formId) {
            form.weez_id = formId;
        }
        form.save({
            eventId: event.pk,
            organizationId: organization.pk
        })
            .then(() => {
                toast.success(t('ticket.form.details.submit.success'));
                goBack();
            })
            .catch(() => {
                toast.error(t('ticket.form.details.submit.error'));
            })
            .finally(() => {
                setIsSubmitting(false);
            });
    }, [formState, formId, formValidate, formErrors, goBack, ratesToSubmit]);

    const link = React.useCallback(
        rates => {
            let tmpRateToUnlink = [...ratesToUnlink];
            let tmpRateToLink = [...ratesToLink];
            rates.forEach(rate => {
                if (tmpRateToUnlink.includes(rate)) {
                    tmpRateToUnlink = tmpRateToUnlink.filter(rateToUnlink => rateToUnlink.pk !== rate.pk);
                } else {
                    tmpRateToLink.push(rate);
                }
            });

            setRatesToLink(tmpRateToLink);
            setRatesToUnlink(tmpRateToUnlink);
        },
        [ratesToUnlink, ratesToLink]
    );

    const languageOptions = React.useMemo(() => {
        let list = event ? event : organization;
        return list.languages.map(lang => {
            return {
                label: LANGUAGES[lang.toLowerCase()],
                value: lang
            };
        });
    }, [organization, event]);

    const defaultLanguage = React.useMemo(() => {
        let defaultLocale = event ? event.default_language : languageOptions.find(it => it.value.includes(organization.default_locale.replace('_', '-').toLowerCase()));
        return event ? defaultLocale : defaultLocale?.value || languageOptions[0]?.value;
    }, [organization, languageOptions, event]);

    const unlink = React.useCallback(
        rates => {
            let tmpRateToUnlink = [...ratesToUnlink];
            let tmpRateToLink = [...ratesToLink];
            rates.forEach(rate => {
                if (tmpRateToLink.includes(rate)) {
                    tmpRateToLink = tmpRateToLink.filter(rateToLink => rateToLink.pk !== rate.pk);
                } else {
                    tmpRateToUnlink.push(rate);
                }
            });

            setRatesToLink(tmpRateToLink);
            setRatesToUnlink(tmpRateToUnlink);
        },
        [ratesToUnlink, ratesToLink]
    );

    if (loading || loadingFields) {
        return <Loading />;
    }

    return (
        <div>
            <WeezHeader
                title={formId && formState.name ? formState.name : t('ticket.common.form.form')}
                backLink={{
                    onClick: () => {
                        goBack();
                        track('ACTION > BACK FORM HOME', { 'Page Title': 'CREATE FORM' });
                    }
                }}
                rightComponent={[
                    <Button
                        primary
                        key={'submit'}
                        onClick={() => {
                            handleSubmit();
                            track('ACTION > SAVE FORM', { 'Page Title': 'CREATE FORM' });
                        }}
                        label={t(`ticket.common.action.save`)}
                        isLoading={isSubmitting}
                    />
                ]}
            />

            {usage && (
                <ModalFields
                    formEntries={formState.form_entries.filter(formEntry => formEntry.usage === usage && formEntry.form_field.is_standard && !formEntry.deleted)}
                    standardFields={fields}
                    customTypes={custom_types}
                    defaultForm={default_form}
                    showModal={!customFieldToEdit}
                    onAddStandardFields={handleStandardFields}
                    onAddCustomField={handleCustomField}
                    onToggleModal={setDisplayFieldsModal}
                    usage={usage}
                    defaultLanguage={defaultLanguage}
                    languageOptions={languageOptions}
                />
            )}
            <ModalCustomField
                formEntry={customFieldToEdit}
                customTypes={custom_types}
                showModal={usage && customFieldToEdit}
                onAddField={handleCustomField}
                onClose={() => {
                    setCustomFieldToEdit(null);
                    setDisplayFieldsModal(null);
                }}
                usage={usage}
                defaultLanguage={defaultLanguage}
                languageOptions={languageOptions}
            />
            <Modal
                allowExternalClosing={false}
                size="large"
                open={showModalRates}
                onClose={() => {
                    setShowModalRates(false);
                    track('ACTION > CLOSE LINKED RATE', { 'Page Title': 'CREATE FORM' });
                }}
                submitButton={
                    <Button
                        primary
                        onClick={() => {
                            link(currentSelectedToLink);
                            setShowModalRates(false);
                            track('ACTION > ADD LINKED RATE', { 'Page Title': 'CREATE FORM' });
                        }}
                        label={t(`ticket.common.action.add`)}
                    />
                }
                cancelButton={
                    <Button
                        onClick={() => {
                            setShowModalRates(false);
                            track('ACTION > RETURN LINKED RATE', { 'Page Title': 'CREATE FORM' });
                        }}
                        label={t(`ticket.common.action.cancel`)}
                    />
                }
                title={t('ticket.form.details.rates.modal.title')}
                subtitle={t('ticket.form.details.rates.modal.subtitle')}
            >
                <RatesManager
                    event={event}
                    organization={organization}
                    initialQuery={{
                        organizationId: organization.id,
                        eventId: event.id
                    }}
                    queryParams={formId ? { forms_exclude: formId } : {}}
                    tableCells={tableCellsModal}
                    mode={'form'}
                    extraRates={ratesToUnlink}
                    excludeRatesIds={ratesToLink.map(rate => rate.pk)}
                    onRatesSelected={setCurrentSelectedToLink}
                    hideEmptyGroup
                />
            </Modal>

            <AnchorNav
                items={[
                    {
                        id: 'name',
                        title: t('ticket.common.informations')
                    },
                    {
                        id: 'attendee',
                        title: t('ticket.common.attendee')
                    },
                    {
                        id: 'buyer',
                        title: t('ticket.common.buyer')
                    },
                    {
                        id: 'rates',
                        title: t('ticket.common.linked_ticket')
                    }
                ]}
                rootId="main"
            >
                <SplitContainer
                    className={Style['details__main_container']}
                    size="maxi"
                    additional={
                        <PreviewCard title={t('ticket.form.preview.title')}>
                            <FormPreview fields={formState?.form_entries} />
                        </PreviewCard>
                    }
                >
                    <FrameCard id="name" title={t('ticket.form.details.name.title')} subtitle={t('ticket.form.details.name.subtitle')}>
                        <Form.Field>
                            <Form.Label>{t('ticket.form.details.name.label')}</Form.Label>
                            <Form.Input
                                errors={Boolean(formErrors['name'])}
                                name="name"
                                value={formState?.name}
                                onChange={({ target: { value } }) => {
                                    if (value.length >= 60) {
                                        // max length
                                        return;
                                    }
                                    onChangeForm('name', value);
                                }}
                                placeholder={t('ticket.form.common.registration')}
                            />
                            <Form.FieldErrors errors={[formErrors['name'] ? t(formErrors['name']) : null]} />
                        </Form.Field>
                    </FrameCard>
                    <FormCard
                        id="attendee"
                        title={t('ticket.form.details.attendee.title')}
                        subtitle={t('ticket.form.details.attendee.subtitle')}
                        emptyTitle={t('ticket.form.details.attendee.empty_title')}
                        onClick={() => {
                            setDisplayFieldsModal('attendee');
                            track('ACTION > ADD FIELD', { 'Page Title': 'CREATE FORM', 'Target type': 'Attendee' });
                        }}
                        objects={formState.form_entries.filter(f => f.usage === 'attendee' && !f.deleted)}
                        emptySubtitle={t('ticket.form.details.attendee.empty_subtitle')}
                        labelButton={t('ticket.form.details.attendee.label')}
                        onToggle={(field_order, attribute, value) => handleStateField(field_order, attribute, value, 'attendee')}
                        onDelete={handleDeleteField}
                        onEdit={field => handleDisplayEditCustomFieldModal(field, 'attendee')}
                        onDragAndDrop={fields => handleDragAndDrop(fields, 'attendee')}
                        defaultFields={default_form.form_entries.filter(f => f.usage === 'attendee' && !f.deletable).map(f => f.form_field.weez_id)}
                    >
                        <FieldsCardContent />
                    </FormCard>
                    <FormCard
                        id="buyer"
                        title={t('ticket.form.details.buyer.title')}
                        subtitle={t('ticket.form.details.buyer.subtitle')}
                        emptyTitle={t('ticket.form.details.buyer.empty_title')}
                        onClick={() => {
                            setDisplayFieldsModal('buyer');
                            track('ACTION > ADD FIELD', { 'Page Title': 'CREATE FORM', 'Target type': 'Buyer' });
                        }}
                        objects={formState.form_entries.filter(f => f.usage === 'buyer' && !f.deleted)}
                        emptySubtitle={t('ticket.form.details.buyer.empty_subtitle')}
                        labelButton={t('ticket.form.details.buyer.label')}
                        onToggle={(field_order, attribute, value) => handleStateField(field_order, attribute, value, 'buyer')}
                        onDelete={handleDeleteField}
                        onEdit={field => handleDisplayEditCustomFieldModal(field, 'buyer')}
                        onDragAndDrop={fields => handleDragAndDrop(fields, 'buyer')}
                        defaultFields={default_form.form_entries.filter(f => f.usage === 'buyer' && !f.deletable).map(f => f.form_field.weez_id)}
                    >
                        <FieldsCardContent />
                    </FormCard>

                    <FormCard
                        id="rates"
                        title={t('ticket.form.details.rates.title')}
                        subtitle={t('ticket.form.details.rates.subtitle')}
                        emptyTitle={t('ticket.form.details.rates.empty_title')}
                        onClick={() => {
                            setShowModalRates(true);
                            track('ACTION > LINK RATE CTA', { 'Page Title': 'CREATE FORM' });
                        }}
                        objects={formState.rates.length > 0 ? formState.rates : ratesToLink}
                        emptySubtitle={t('ticket.form.details.rates.subtitle')}
                        labelButton={t('ticket.form.details.rates.label')}
                    >
                        <RatesManager
                            event={event}
                            organization={organization}
                            initialQuery={{
                                organizationId: organization.id,
                                eventId: event.id
                            }}
                            queryParams={{ forms: formId || '999999999999999' }}
                            tableCells={tableCells}
                            mode={'form'}
                            extraRates={ratesToLink}
                            excludeRatesIds={ratesToUnlink.map(rate => rate.pk)}
                            onRatesSelected={setCurrentSelectedToUnlink}
                            selected={currentSelectedToUnlink}
                            hideEmptyGroup
                        >
                            <div className={Style['details__container-actions']}>
                                <SelectButton
                                    tooltipTitle={t('ticket.rates.noActionTooltip')}
                                    title={
                                        currentSelectedToUnlink.length > 0
                                            ? t('ticket.rates.rateAction', { count: currentSelectedToUnlink.length })
                                            : t('ticket.common.action.bulk')
                                    }
                                    objects={currentSelectedToUnlink}
                                >
                                    <Dropdown.Item
                                        disabled={currentSelectedToUnlink.length === 0}
                                        onClick={() => {
                                            unlink(currentSelectedToUnlink);
                                            setCurrentSelectedToUnlink([]);
                                        }}
                                        deleteItem
                                    >
                                        {t('ticket.common.action.delete')}
                                    </Dropdown.Item>
                                </SelectButton>
                                <Button
                                    primary
                                    inverted
                                    onClick={() => {
                                        setShowModalRates(true);
                                        track('ACTION > LINK RATE', { 'Page Title': 'CREATE FORM' });
                                    }}
                                >
                                    <Icon name={'plus'} className={Style['plus']} />
                                    {t('ticket.form.details.rates.label')}
                                </Button>
                            </div>
                        </RatesManager>
                    </FormCard>
                </SplitContainer>
            </AnchorNav>
        </div>
    );
}

Details.propTypes = {
    event: PropTypes.object.isRequired,
    organization: PropTypes.object.isRequired,
    formId: PropTypes.number
};
